import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  TextField,
  Tooltip,
  IconButton,
  useTheme,
  MenuItem,
  useMediaQuery 
} from '@mui/material';
import { forwardRef, useState, useEffect, ReactElement, ChangeEvent, Ref } from 'react';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CloseIcon from '@mui/icons-material/Close';
import { sendSupportMessage, getSupportSubjects } from 'src/services/supportService';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { HelpBoxTypeEnum } from './enum/HelpBoxTypeEnum';
import { supportPayload } from './interface/types';
import ConfirmationModal from 'src/content/common/confirmationModal/ConfirmationModal';



const Transition = forwardRef(function Transition(
  props: TransitionProps & { children?: ReactElement },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogWrapper = styled(Dialog)(() => `
  .MuiPaper-root {
    border-radius: 22px !important;
    overflow: hidden; /* Clip title bar to match corners */
  }

  .MuiDialog-container {
    align-items: center;
  }

  .MuiDialog-paperScrollPaper {
    max-height: none;
  }
`);


const DialogTitleWrapper = styled(DialogTitle)(({ theme }) => `
  background: ${theme.palette.background.default}; // 
  padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
`);

const HelpBox = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const primaryColor = '#002244';
  const headerGray = theme.palette.background.default;
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<HelpBoxTypeEnum>(HelpBoxTypeEnum.SUGGESTION);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [subjectOptions, setSubjectOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [messageError, setMessageError] = useState('');

  const handleClickOpen = () => setOpen(true);
  const confirmAndClose = () => {
  if (message?.length > 0 || subject) {
  setOpenConfirmation(true);
} else {
  setOpen(false);
  resetForm();
}
  };


  const resetForm = () => {
    setType(HelpBoxTypeEnum.SUGGESTION);
    setMessage('');
    setSubject('');
    setSubjectError('');
    setMessageError('');
  };

  const handleSubmit = async () => {

    const payload: supportPayload = {
      requestType: type,
      message,
      subject
    };

    if(subject.trim() == '' ) {
      setSubjectError('Subject Is Required');
    }else{
      setSubjectError('');
    }

    if(message.trim() == '' ) {
      setMessageError('Details Of Request Is Required');
    }else{
      setMessageError('');
    }

    if(subject.trim() == '' || message.trim() == ''){
      return;
    }

    setLoading(true);

    const formatSubject = (text: string) => {
      const words = text.trim().split(" ");
      const firstWord = words[0]?.charAt(0).toUpperCase() + words[0]?.slice(1).toLowerCase();
      const rest = words.slice(1).map(word => word.toLowerCase()).join(" ");
      return `${firstWord}${rest ? " " + rest : ""}`;
    };

    try {
      const response = await sendSupportMessage(payload);

      if (response.status === 200) {
        setSnackbarSeverity('success');
        setSnackBarMessage(`${type} - ${formatSubject(subject)} request sent successfully!`);
        setSnackBarOpen(true);
        setOpenConfirmation(false);
        setOpen(false);
        resetForm();

        
      } else {
        setSnackbarSeverity('error');
        setSnackBarMessage(`Failed to send ${type} - ${subject.toLowerCase()} request`);
        setSnackBarOpen(true);
      }
    } catch (error: any) {
      setSnackBarMessage(`Failed to send ${type} - ${subject.toLowerCase()} request.`);
      setSnackbarSeverity('error');
      setSnackBarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await getSupportSubjects();
        const subjects = response.data?.data.map((item: { name: string }) => item.name);
        setSubjectOptions(subjects);
      } catch (error) {
        console.error('Error fetching subjects', error);
      }
    };

    fetchSubjects();
  }, []);

 const getPaperSx = () => ({
  borderRadius: 3,
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(60,60,60,0.18)',
  background: '#f9f9fb',
  width: {
    xs: '88vw',
    sm: '80vw',
    md: 460,
    lg: 500,
    xl: 670
  },
  minHeight: 320,
  
  height: 'auto',
  maxWidth: '100vw',
  maxHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  m: 'auto',
  p: 0
});


  return (
    <>
      <Tooltip arrow title="Suggestion/Help">
        <Box className="header-helper-btn" onClick={handleClickOpen}>
          <Inventory2OutlinedIcon className="icon-header-helper" 
            sx={{
              fontSize: { xs: 17, sm: 18, md: 19, lg: 21, xl: 23 }
            }}
          />
        </Box>
      </Tooltip>

      <DialogWrapper
        open={open}
        TransitionComponent={Transition}
        keepMounted
        fullScreen={fullScreen}
        maxWidth={false}
        fullWidth
         onClose={confirmAndClose}
        PaperProps={{
          sx: getPaperSx()
        }}
      >
        <DialogTitleWrapper>
          <Typography variant="h5" sx={{ color: primaryColor }}>
            Suggestion/Help
          </Typography>
          <IconButton onClick={confirmAndClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitleWrapper>

        <DialogContent
          sx={{
            overflow: 'visible',
            paddingBottom: 3
          }}
        >
          <Box display="flex" flexDirection="column" gap={1} mt={1}>
            <RadioGroup
              row
              value={type}
              onChange={(e) => setType(e.target.value as HelpBoxTypeEnum)}
              sx={{ mb: 1 }}
            >
              <FormControlLabel
                value={HelpBoxTypeEnum.SUGGESTION}
                control={
                  <Radio/>
                }
                label="Suggestion"
              />
              <FormControlLabel
                value={HelpBoxTypeEnum.HELP}
                control={
                  <Radio/>
                }
                label="Help"
              />
            </RadioGroup>

            <Typography variant="subtitle1" sx={{ color: primaryColor }}>
              Subject <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              select
              size="small"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  '& fieldset': {
                    borderColor: '#ccc' 
                  },
                  '&:hover fieldset': {
                    borderColor: '#999' 
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3366FF', 
                    borderWidth: '2px'
                  }
                },
                '& .MuiSelect-select': {
                  padding: '10px 14px',
                  color: subject ? 'black' : '#9e9e9e'
                },
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected) =>
                !selected ? <span style={{ color: '#9e9e9e' }}>Select subject</span> : selected,
                MenuProps: {
                  PaperProps: {
                    style: { maxHeight: 160, overflowY: 'auto' },
                  },
                  MenuListProps: {
                    style: { maxHeight: 160, overflowY: 'auto' },
                  },
                },
              }}
            >
              {subjectOptions.map((option, index) => (
                <MenuItem key={index} value={option} sx={{ paddingY: '10px', paddingX: '16px' }}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <Typography
              style={{
                paddingRight: 15,
                fontSize: '11px',
                textTransform: 'capitalize'
              }}
              color="red"
            >
              {subjectError}
            </Typography>

            <Typography variant="subtitle1" sx={{ color: primaryColor }}>
              Details of request <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              placeholder="Enter details"
              fullWidth
              multiline
              minRows={5}
              maxRows={5}
              value={message}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setMessage(e.target.value)}
              InputProps={{
                sx: {
                  backgroundColor: headerGray,
                  borderRadius: '8px'
                }
              }}
            />
            <Typography
              style={{
                paddingRight: 15,
                fontSize: '11px',
                textTransform: 'capitalize'
              }}
              color="red"
            >
              {messageError}
            </Typography>

            <Box display="flex" justifyContent="flex-end">
              <Button
                id="btn-toggleConfirmation"
                type="button"
                onClick={handleSubmit}
                sx={{
                  ml: 2,
                  px: 4,
                  fontSize: '13px',
                  fontWeight: 'normal',
                  background: '#0069E4'
                }}
                variant="contained"
              >
                {loading ? 'Sending...' : 'Submit'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </DialogWrapper>

           <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackbarSeverity={snackbarSeverity}
        snackBarMessage={snackBarMessage}
      />

      <ConfirmationModal
        isLoading={false}
        open={openConfirmation}
        toggle={() => setOpenConfirmation(false)}
        message={'You have unsaved changes. Are you sure you want to close and discard them?'}
        onConfirm={() => {
          setOpenConfirmation(false);
          setOpen(false);
          resetForm();
        }}
        noToggle={() => setOpenConfirmation(false)}
      />
    </>
  );
};

export default HelpBox;


